/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { queryCache } from 'react-query';

import { Endpoints, ProjectRole } from 'shared';

import { ApiClient } from '../types/api/ApiClient';
import { EntityId } from '../types/api/EntityId';
import { getApiEndpoint } from '../utils/getApiEndpoint';
import { apiClientFactory } from '../utils/http-client/apiClientFactory';
import { axiosInstanceFactory } from '../utils/http-client/axiosInstanceFactory';
import { axiosJwtTokenInterceptor, axiosUnauthorizedInterceptor } from '../utils/http-client/axiosInterceptors';
import { fetchProject } from '../utils/queryFns';

const invalidateQueriesAndReturn = async <T>(response: T) => {
  await queryCache.invalidateQueries(fetchProject.key);
  return response;
};

class ProjectUserApiServiceClass {
  private api: ApiClient;

  constructor() {
    this.api = apiClientFactory(
      axiosInstanceFactory(
        { baseURL: getApiEndpoint() },
        [axiosJwtTokenInterceptor],
        [[undefined, axiosUnauthorizedInterceptor]],
      ),
    );
  }

  add = ({
    organizationId,
    projectId,
    userIds,
    teamIdentifiers,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    userIds: EntityId[];
    teamIdentifiers: string[];
  }) =>
    this.api
      .post(Endpoints.PostProjectUsers.route({ organizationId, projectId }), {
        users: userIds.map(id => ({ id })),
        teams: teamIdentifiers.map(identifier => ({ identifier })),
      })
      .then(invalidateQueriesAndReturn);

  update = ({
    organizationId,
    projectId,
    target,
    role,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    target: { userId: string } | { teamIdentifier: string };
    role: ProjectRole;
  }) =>
    this.api
      .patch(
        'userId' in target
          ? Endpoints.PatchProjectUser.route({ organizationId, projectId, ...target })
          : Endpoints.PatchProjectTeam.route({ organizationId, projectId, ...target }),
        { role },
      )
      .then(invalidateQueriesAndReturn);

  remove = ({
    organizationId,
    projectId,
    target,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    target: { userId: string } | { teamIdentifier: string };
  }) =>
    this.api
      .del(
        'userId' in target
          ? Endpoints.DeleteProjectUser.route({ organizationId, projectId, ...target })
          : Endpoints.DeleteProjectTeam.route({ organizationId, projectId, ...target }),
      )
      .then(invalidateQueriesAndReturn);
}

export const ProjectUserApiService = new ProjectUserApiServiceClass();
